import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { employeeApi, Employee } from '../services/employeeService.ts';
import { eventFormsApi, EventForm } from '../services/eventFormsService.ts';
import { TableSetUpCanvas } from '../components/TableSetUpCanvas.tsx';

const EventFormPage: React.FC = () => {
  const { showSuccess, showError } = useSnackbar();
  const { sessionToken } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const eventFormId = searchParams.get('id');

  // Form state
  const [formData, setFormData] = useState({
    status: null as string | null,
    eventDate: '',
    setUpDecorate: '',
    guestArrival: '',
    food: '',
    servers: [] as number[], // Array of employee IDs
    name: '',
    eventFor: '',
    phone: '',
    email: '',
    depositDateManager: '',
    time: '',
    room: '',
    event: '',
    expGuest: null as number | null,
    finalCount: null as number | null,
    menu: [] as string[], // Array of menu options
    chalkboardMessage: '',
    beverages: '',
    tables: '',
    linen: '',
    misc: '',
    tableSetUp: '',
  });

  // Employees data
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState<Employee[]>([]);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Menu row input
  const [menuInput, setMenuInput] = useState('');

  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveError, setAutoSaveError] = useState('');
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, []);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!sessionToken) return;

      setEmployeesLoading(true);
      try {
        const response = await employeeApi.getEmployees({ page: 1, limit: 1000 }, sessionToken);
        setEmployees(response.data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch employees';
        showError(errorMessage);
      } finally {
        setEmployeesLoading(false);
      }
    };

    fetchEmployees();
  }, [sessionToken]);

  // Fetch event form data if editing
  useEffect(() => {
    const fetchEventForm = async () => {
      if (!eventFormId || !sessionToken || employeesLoading) return;

      setLoading(true);
      try {
        const data = await eventFormsApi.getEventForm(parseInt(eventFormId), sessionToken);
        
        // Parse servers (JSON string to array)
        let serverIds: number[] = [];
        let selectedEmps: Employee[] = [];
        if (data.servers) {
          try {
            serverIds = JSON.parse(data.servers);
            selectedEmps = employees.filter(emp => serverIds.includes(emp.id));
          } catch (e) {
            console.error('Error parsing servers:', e);
          }
        }

        // Parse menu (JSON string to array)
        let menuOptions: string[] = [];
        if (data.menu) {
          try {
            menuOptions = JSON.parse(data.menu);
          } catch (e) {
            console.error('Error parsing menu:', e);
          }
        }

        setFormData({
          status: data.status ?? null,
          eventDate: data.eventDate || '',
          setUpDecorate: data.setUpDecorate || '',
          guestArrival: data.guestArrival || '',
          food: data.food || '',
          servers: serverIds,
          name: data.name || '',
          eventFor: data.eventFor || '',
          phone: data.phone || '',
          email: data.email || '',
          depositDateManager: data.depositDateManager || '',
          time: data.time || '',
          room: data.room || '',
          event: data.event || '',
          expGuest: data.expGuest ?? null,
          finalCount: data.finalCount ?? null,
          menu: menuOptions,
          chalkboardMessage: data.chalkboardMessage || '',
          beverages: data.beverages || '',
          tables: data.tables || '',
          linen: data.linen || '',
          misc: data.misc || '',
          tableSetUp: data.tableSetUp || '',
        });
        setSelectedEmployees(selectedEmps);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch event form';
        showError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (eventFormId && !employeesLoading && employees.length >= 0) {
      fetchEventForm();
    }
  }, [eventFormId, sessionToken, employees, employeesLoading]);

  // Prefill form when navigating from list "Copy" (create new from existing record)
  useEffect(() => {
    const copyFrom = location.state?.copyFrom as EventForm | undefined;
    if (eventFormId || !copyFrom || employeesLoading) return;

    let serverIds: number[] = [];
    if (copyFrom.servers) {
      try {
        serverIds = JSON.parse(copyFrom.servers);
      } catch (e) {
        console.error('Error parsing servers for copy:', e);
      }
    }
    let menuOptions: string[] = [];
    if (copyFrom.menu) {
      try {
        menuOptions = JSON.parse(copyFrom.menu);
      } catch (e) {
        console.error('Error parsing menu for copy:', e);
      }
    }

    setFormData({
      status: copyFrom.status ?? null,
      eventDate: copyFrom.eventDate || '',
      setUpDecorate: copyFrom.setUpDecorate || '',
      guestArrival: copyFrom.guestArrival || '',
      food: copyFrom.food || '',
      servers: serverIds,
      name: copyFrom.name || '',
      eventFor: copyFrom.eventFor || '',
      phone: copyFrom.phone || '',
      email: copyFrom.email || '',
      depositDateManager: copyFrom.depositDateManager || '',
      time: copyFrom.time || '',
      room: copyFrom.room || '',
      event: copyFrom.event || '',
      expGuest: copyFrom.expGuest ?? null,
      finalCount: copyFrom.finalCount ?? null,
      menu: menuOptions,
      chalkboardMessage: copyFrom.chalkboardMessage || '',
      beverages: copyFrom.beverages || '',
      tables: copyFrom.tables || '',
      linen: copyFrom.linen || '',
      misc: copyFrom.misc || '',
      tableSetUp: copyFrom.tableSetUp || '',
    });
    setSelectedEmployees(employees.filter((emp) => serverIds.includes(emp.id)));
    navigate(location.pathname, { replace: true, state: {} });
  }, [eventFormId, location.state?.copyFrom, location.pathname, employeesLoading, employees, navigate]);

  const validateFormData = (data: typeof formData): string | null => {
    if (!data.eventDate) return 'Event Date is required';
    if (!data.phone.trim()) return 'Phone Number is required';
    if (!data.email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) return 'Please enter a valid Email address';
    return null;
  };

  const buildApiData = (data: typeof formData) => ({
    status: data.status ?? null,
    eventDate: data.eventDate,
    setUpDecorate: data.setUpDecorate || null,
    guestArrival: data.guestArrival || null,
    food: data.food || null,
    servers: data.servers.length > 0 ? JSON.stringify(data.servers) : null,
    name: data.name || null,
    eventFor: data.eventFor || null,
    phone: data.phone.trim(),
    email: data.email.trim(),
    depositDateManager: data.depositDateManager || null,
    time: data.time || null,
    room: data.room || null,
    event: data.event || null,
    expGuest: data.expGuest ?? null,
    finalCount: data.finalCount ?? null,
    menu: data.menu.length > 0 ? JSON.stringify(data.menu) : null,
    chalkboardMessage: data.chalkboardMessage || null,
    beverages: data.beverages || null,
    tables: data.tables || null,
    linen: data.linen || null,
    misc: data.misc || null,
    tableSetUp: data.tableSetUp || null,
  });

  // Auto-saves ~900ms after the last change to any field, while editing an existing
  // event form. Creating a new one still requires the explicit Save button.
  const performAutoSave = useCallback(
    async (id: number, snapshot: typeof formData) => {
      if (!sessionToken) return;
      const validationError = validateFormData(snapshot);
      if (validationError) {
        setAutoSaveStatus('error');
        setAutoSaveError(validationError);
        return;
      }
      setAutoSaveStatus('saving');
      try {
        await eventFormsApi.updateEventForm(id, buildApiData(snapshot), sessionToken);
        setAutoSaveStatus('saved');
      } catch (err) {
        setAutoSaveStatus('error');
        setAutoSaveError(err instanceof Error ? err.message : 'Failed to save changes');
      }
    },
    [sessionToken]
  );

  const scheduleAutoSave = (next: typeof formData) => {
    if (!eventFormId) return;
    const id = parseInt(eventFormId, 10);
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    setAutoSaveStatus('pending');
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveTimeoutRef.current = null;
      performAutoSave(id, next);
    }, 900);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      scheduleAutoSave(next);
      return next;
    });
  };

  const handleEmployeesChange = (selected: Employee[]) => {
    setSelectedEmployees(selected);
    const ids = selected.map(emp => emp.id);
    handleInputChange('servers', ids);
  };

  const handleAddMenuOption = () => {
    if (menuInput.trim()) {
      setFormData((prev) => {
        const next = { ...prev, menu: [...prev.menu, menuInput.trim()] };
        scheduleAutoSave(next);
        return next;
      });
      setMenuInput('');
    }
  };

  const handleRemoveMenuOption = (index: number) => {
    setFormData((prev) => {
      const next = { ...prev, menu: prev.menu.filter((_, i) => i !== index) };
      scheduleAutoSave(next);
      return next;
    });
  };

  const handleSave = async () => {
    if (!sessionToken || saving) return;

    const validationError = validateFormData(formData);
    if (validationError) {
      showError(validationError);
      return;
    }

    setSaving(true);
    try {
      const apiData = buildApiData(formData);

      if (eventFormId) {
        // Update existing
        await eventFormsApi.updateEventForm(parseInt(eventFormId), apiData, sessionToken);
        showSuccess('Event form updated successfully!');
      } else {
        // Create new
        await eventFormsApi.createEventForm(apiData, sessionToken);
        showSuccess('Event form created successfully!');
      }

      // Close the form and return to the list
      navigate('/event-forms-list');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save event form';
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Typography variant="h6" sx={{ color: '#7f0000', fontWeight: 700, flex: '0 0 auto', whiteSpace: 'nowrap' }}>
          Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Event Form
        </Typography>
      </Box>
        <Paper sx={{ p: 3 }}>
        {/* Header with Status, Event Date and Save Button */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
          <FormControl sx={{ minWidth: 180 }} size="medium">
            <InputLabel id="event-form-status-label">Status</InputLabel>
            <Select
              labelId="event-form-status-label"
              label="Status"
              value={formData.status ?? ''}
              onChange={(e) => handleInputChange('status', e.target.value === '' ? null : e.target.value)}
            >
              <MenuItem value="">
                <em>—</em>
              </MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Confirmed">Confirmed</MenuItem>
              <MenuItem value="Cancelled">Cancelled</MenuItem>
              <MenuItem value="Missing Information">Missing Information</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Event Date"
            type="date"
            value={formData.eventDate}
            onChange={(e) => handleInputChange('eventDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
            sx={{ width: 250 }}
          />
          <Box sx={{ flex: 1 }} />
          {eventFormId && (
            <Typography
              variant="body2"
              color={autoSaveStatus === 'error' ? 'error' : 'text.secondary'}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {autoSaveStatus === 'saving' && (<><CircularProgress size={14} />Saving…</>)}
              {autoSaveStatus === 'pending' && 'Unsaved changes…'}
              {autoSaveStatus === 'saved' && 'All changes saved'}
              {autoSaveStatus === 'error' && (autoSaveError || 'Failed to save changes')}
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            size="large"
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* First Row: Set up/Decorate, Guest Arrival, Food */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Set up/Decorate"
              value={formData.setUpDecorate}
              onChange={(e) => handleInputChange('setUpDecorate', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Guest Arrival"
              value={formData.guestArrival}
              onChange={(e) => handleInputChange('guestArrival', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Food"
              value={formData.food}
              onChange={(e) => handleInputChange('food', e.target.value)}
            />
          </Grid>
        </Grid>

        {/* Second Row: Server(s) - Multi-select dropdown */}
        <Box sx={{ mb: 3 }}>
          <Autocomplete
            multiple
            options={employees}
            getOptionLabel={(option) => option.name}
            value={selectedEmployees}
            onChange={(event, newValue) => handleEmployeesChange(newValue)}
            loading={employeesLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Server(s)"
                placeholder="Select employees"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name}
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
          />
        </Box>

        {/* Third Row: Section with vertical fields */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Event Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Who is Event for"
                value={formData.eventFor}
                onChange={(e) => handleInputChange('eventFor', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Deposit Date and Manager"
                value={formData.depositDateManager}
                onChange={(e) => handleInputChange('depositDateManager', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Fourth Row: Time, Room, Event, Exp Guest, Final Count */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Time"
              value={formData.time}
              onChange={(e) => handleInputChange('time', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Room"
              value={formData.room}
              onChange={(e) => handleInputChange('room', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Event"
              value={formData.event}
              onChange={(e) => handleInputChange('event', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Exp Guest"
              type="number"
              value={formData.expGuest ?? ''}
              onChange={(e) => handleInputChange('expGuest', e.target.value ? parseInt(e.target.value, 10) : null)}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Final Count"
              type="number"
              value={formData.finalCount ?? ''}
              onChange={(e) => handleInputChange('finalCount', e.target.value ? parseInt(e.target.value, 10) : null)}
              inputProps={{ min: 0 }}
            />
          </Grid>
        </Grid>

        {/* Fifth Row: Menu Section */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Menu Options
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Menu option"
              value={menuInput}
              onChange={(e) => setMenuInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddMenuOption();
                }
              }}
              placeholder="Enter menu option and press Enter or click Add"
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddMenuOption}
              disabled={!menuInput.trim()}
            >
              Add
            </Button>
          </Box>
          {formData.menu.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Added Menu Options:
              </Typography>
              <List dense>
                {formData.menu.map((option, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleRemoveMenuOption(index)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor: 'background.paper',
                    }}
                  >
                    <ListItemText primary={option} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>

        {/* Sixth Row: Chalkboard Message */}
        <TextField
          fullWidth
          label="Chalkboard Message"
          value={formData.chalkboardMessage}
          onChange={(e) => handleInputChange('chalkboardMessage', e.target.value)}
          multiline
          rows={2}
          sx={{ mb: 3 }}
        />

        {/* Seventh Row: Beverages */}
        <TextField
          fullWidth
          label="Beverages (Open bar/pitchers, checks)"
          value={formData.beverages}
          onChange={(e) => handleInputChange('beverages', e.target.value)}
          multiline
          rows={2}
          sx={{ mb: 3 }}
        />

        {/* Eighth Row: Tables */}
        <TextField
          fullWidth
          label="Tables (Gift, Cake, check-in)"
          value={formData.tables}
          onChange={(e) => handleInputChange('tables', e.target.value)}
          multiline
          rows={2}
          sx={{ mb: 3 }}
        />

        {/* Ninth Row: Linen(s) */}
        <TextField
          fullWidth
          label="Linen(s)"
          value={formData.linen}
          onChange={(e) => handleInputChange('linen', e.target.value)}
          sx={{ mb: 3 }}
        />

        {/* Tenth Row: Misc */}
        <TextField
          fullWidth
          label="Misc"
          value={formData.misc}
          onChange={(e) => handleInputChange('misc', e.target.value)}
          multiline
          rows={2}
          sx={{ mb: 3 }}
        />

        {/* Eleventh Row: Table Set Up */}
        <TableSetUpCanvas
          value={formData.tableSetUp}
          onChange={(v) => handleInputChange('tableSetUp', v)}
          disabled={saving}
        />
      </Paper>
    </Box>
  );
};

export default EventFormPage;

